import { useMemo, useState } from 'react'
import {Copy, RotateCcw} from 'lucide-react'
import toast from 'react-hot-toast'
import Breadcrumbs from '@/components/Breadcrumbs'
import DisclaimerNote from '@/components/DisclaimerNote'
import FaqAccordion from '@/components/FaqAccordion'
import NumberInput from '@/components/ui/NumberInput'
import ProgressBar from '@/components/ui/ProgressBar'
import SectionHeading from '@/components/ui/SectionHeading'
import StatCard from '@/components/ui/StatCard'
import Seo from '@/components/Seo'
import { formatCurrency } from '@/lib/format'
import { breadcrumbSchema, faqPageSchema, softwareApplicationSchema } from '@/lib/schema'

interface NetWorthState {
  cash: number
  investments: number
  retirement: number
  realEstate: number
  vehicles: number
  otherAssets: number
  mortgage: number
  studentLoans: number
  autoLoans: number
  creditCards: number
  otherDebt: number
}

const defaults: NetWorthState = {
  cash: 8000,
  investments: 25000,
  retirement: 42000,
  realEstate: 320000,
  vehicles: 18000,
  otherAssets: 3000,
  mortgage: 240000,
  studentLoans: 22000,
  autoLoans: 9000,
  creditCards: 3500,
  otherDebt: 1500,
}

const faqItems = [
  { question: 'What is net worth and how is it calculated?', answer: 'Net worth equals total assets minus total liabilities. Assets include cash, investments, retirement accounts, real estate, and vehicles. Liabilities include mortgages, loans, and credit card balances.' },
  { question: 'What is a good net worth for my age?', answer: 'A common benchmark is having 1x your annual salary saved by age 30, 3x by age 40, and 6x by age 50, though this varies significantly by location and income.' },
  { question: 'Should I include my home equity in net worth?', answer: 'Yes, your home\'s market value minus your remaining mortgage balance (home equity) should be included as a real estate asset.' },
]

function NetWorth() {
  const [state, setState] = useState<NetWorthState>(defaults)
  const update = (key: keyof NetWorthState) => (value: number) => setState(prev => ({ ...prev, [key]: value }))

  const results = useMemo(() => {
    const totalAssets = state.cash + state.investments + state.retirement + state.realEstate + state.vehicles + state.otherAssets
    const totalLiabilities = state.mortgage + state.studentLoans + state.autoLoans + state.creditCards + state.otherDebt
    const netWorth = totalAssets - totalLiabilities
    return { totalAssets, totalLiabilities, netWorth }
  }, [state])

  const handleReset = () => {
    setState(defaults)
    toast.success('Reset to default values')
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(`Mint Calculator - Net Worth Summary\nTotal Assets: ${formatCurrency(results.totalAssets)}\nTotal Liabilities: ${formatCurrency(results.totalLiabilities)}\nNet Worth: ${formatCurrency(results.netWorth)}`)
    toast.success('Summary copied to clipboard')
  }

  return (
    <>
      <Seo
        title="Net Worth Calculator - Track Assets vs Liabilities"
        description="Free net worth calculator. Track cash, investments, retirement, and real estate against mortgages, loans, and credit card debt to find your true net worth."
        path="/calculators/net-worth"
        schema={[
          softwareApplicationSchema('Net Worth Calculator', 'Calculate total net worth from assets and liabilities.', 'https://www.mintcalculator.com/calculators/net-worth'),
          faqPageSchema(faqItems),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Net Worth Calculator', path: '/calculators/net-worth' }]),
        ]}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Net Worth Calculator', path: '/calculators/net-worth' }]} />
        <SectionHeading
          eyebrow="Personal Budgeting & Net Worth"
          title="Net Worth Calculator"
          description="Add up your assets and liabilities to see exactly where you stand financially, updated in real time."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-3">
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-400">Assets</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberInput label="Cash & Checking" value={state.cash} onChange={update('cash')} prefix="$" step={500} />
                <NumberInput label="Investments" value={state.investments} onChange={update('investments')} prefix="$" step={500} />
                <NumberInput label="Retirement (401k/IRA)" value={state.retirement} onChange={update('retirement')} prefix="$" step={500} />
                <NumberInput label="Real Estate Value" value={state.realEstate} onChange={update('realEstate')} prefix="$" step={5000} />
                <NumberInput label="Vehicles" value={state.vehicles} onChange={update('vehicles')} prefix="$" step={500} />
                <NumberInput label="Other Assets" value={state.otherAssets} onChange={update('otherAssets')} prefix="$" step={500} />
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-rose-400">Liabilities</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberInput label="Mortgage Balance" value={state.mortgage} onChange={update('mortgage')} prefix="$" step={1000} accent="amber" />
                <NumberInput label="Student Loans" value={state.studentLoans} onChange={update('studentLoans')} prefix="$" step={500} accent="amber" />
                <NumberInput label="Auto Loans" value={state.autoLoans} onChange={update('autoLoans')} prefix="$" step={500} accent="amber" />
                <NumberInput label="Credit Card Debt" value={state.creditCards} onChange={update('creditCards')} prefix="$" step={100} accent="amber" />
                <NumberInput label="Other Debt" value={state.otherDebt} onChange={update('otherDebt')} prefix="$" step={100} accent="amber" />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={handleCopy} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <Copy className="h-4 w-4" />
                Copy Summary
              </button>
              <button onClick={handleReset} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <RotateCcw className="h-4 w-4" />
                Reset Defaults
              </button>
            </div>
          </div>

          <div className="space-y-6 lg:col-span-2">
            <StatCard label="Net Worth" value={formatCurrency(results.netWorth)} accent={results.netWorth >= 0 ? 'emerald' : 'rose'} size="lg" />
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Total Assets" value={formatCurrency(results.totalAssets)} accent="emerald" />
              <StatCard label="Total Liabilities" value={formatCurrency(results.totalLiabilities)} accent="rose" />
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold text-slate-300">Assets vs Liabilities</h3>
              <div className="mt-4">
                <ProgressBar
                  total={results.totalAssets + results.totalLiabilities || 1}
                  segments={[
                    { label: 'Assets', value: results.totalAssets, color: 'bg-emerald-500' },
                    { label: 'Liabilities', value: results.totalLiabilities, color: 'bg-rose-500' },
                  ]}
                />
              </div>
            </div>
            <DisclaimerNote />
          </div>
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="mt-6">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </div>
    </>
  )
}

export default NetWorth
